import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { Icon } from "@components/icon"
import { sortAscending } from "@utils/general"
import { isValidArray } from "@utils/variables.helpers"

import * as styles from "./flyout.module.css"

/**
 * The rows a top-level item's flyout lists in its first column.
 */
export const flyoutRows = (navigationItem) =>
  sortAscending([...(navigationItem?.children || [])], "sortOrder")

/**
 * The row whose subcategories show when the flyout opens. A lone row (Hire
 * Online -> Hire Online) is opened straight away, rather than leaving the
 * second column empty until it is hovered.
 */
const initialActiveRowId = (rows) =>
  rows.length === 1 && isValidArray(rows[0].children) ? rows[0].id : null

// Closest the flyout may come to either side of the viewport.
const VIEWPORT_MARGIN = 16

/**
 * Where the flyout's left edge goes. It hangs from the left of the link that
 * opened it. Near the end of the bar, where that would run off the screen,
 * the first column lines up with the link's right edge instead, as a dropdown
 * would. Either way the whole flyout, including room for the subcategory
 * column to its right, is then kept inside the viewport - so it is placed
 * once per opening and doesn't jump when a row is hovered.
 *
 * Pass `subcolumnWidth: 0` when no row has subcategories.
 */
export const flyoutLeft = ({
  linkLeft,
  linkRight,
  columnWidth,
  subcolumnWidth,
  viewportWidth,
}) => {
  const max = viewportWidth - VIEWPORT_MARGIN
  const width = columnWidth + subcolumnWidth
  const preferred =
    linkLeft + width <= max
      ? linkLeft
      : Math.min(linkLeft, linkRight - columnWidth)
  return Math.max(VIEWPORT_MARGIN, Math.min(preferred, max - width))
}

// useLayoutEffect warns during SSR; the flyout only measures in the browser.
const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect

/**
 * The desktop category flyout: secondary categories in the first column and,
 * for the row being hovered or focused, its subcategories in a second one to
 * the right.
 *
 * Every link is rendered up front and only toggled with `hidden`, so the
 * static HTML carries the same internal links the old full-width dropdown
 * did. Each subcategory list sits inside its row's <li>, which keeps the tab
 * order natural: a row, then its subcategories, then the next row.
 *
 * The two columns scroll independently. The subcategory column is laid over
 * the flyout's full height, and the flyout grows (up to the screen) when a
 * subcategory list is taller than the first column.
 */
const Flyout = forwardRef(function Flyout(
  { id, rows, open, position, onCategoryClick },
  ref
) {
  const [activeRowId, setActiveRowId] = useState(() => initialActiveRowId(rows))
  const rootRef = useRef(null)
  const subcolumnRefs = useRef({})

  // Every opening starts from the same row state.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setActiveRowId(initialActiveRowId(rows))
    }
  }

  const activeRow = rows.find((row) => row.id === activeRowId)
  const showsSubcolumn = isValidArray(activeRow?.children)
  const maxHeight = `calc(100vh - ${position.top}px - ${VIEWPORT_MARGIN}px)`

  // Let the flyout grow to fit the active subcategory list. Measured against
  // the flyout's natural height (the first column), so moving to a shorter
  // list shrinks it back.
  useIsomorphicLayoutEffect(() => {
    const root = rootRef.current
    if (!root) {
      return
    }
    root.style.minHeight = ""
    const subcolumn = subcolumnRefs.current[activeRowId]
    if (open && showsSubcolumn && subcolumn) {
      root.style.minHeight = `min(${subcolumn.scrollHeight}px, ${maxHeight})`
    }
  }, [open, activeRowId, showsSubcolumn, maxHeight])

  const setRootRef = (el) => {
    rootRef.current = el
    if (typeof ref === "function") {
      ref(el)
    } else if (ref) {
      ref.current = el
    }
  }

  return (
    <div
      id={id}
      ref={setRootRef}
      hidden={!open}
      className={`${styles.flyout} ${showsSubcolumn ? styles.withSubcolumn : ""}`}
      style={{ top: position.top, left: position.left, maxHeight }}
    >
      <ul className={styles.column}>
        {rows.map((row) => {
          const hasChildren = isValidArray(row.children)
          const isActive = row.id === activeRowId
          return (
            <li key={row.id}>
              <a
                href={row.link}
                className={`${styles.row} ${isActive ? styles.rowActive : ""}`}
                onMouseEnter={() => setActiveRowId(row.id)}
                onFocus={() => setActiveRowId(row.id)}
                onClick={() => onCategoryClick(row)}
              >
                <span>{row.name}</span>
                {hasChildren && (
                  <Icon
                    id="chevron-right"
                    className={styles.rowChevron}
                    aria-hidden="true"
                    focusable="false"
                  />
                )}
              </a>
              {hasChildren && (
                <ul
                  className={styles.subcolumn}
                  hidden={!isActive}
                  ref={(el) => {
                    subcolumnRefs.current[row.id] = el
                  }}
                >
                  {sortAscending([...row.children], "sortOrder").map(
                    (child) => (
                      <li key={child.id}>
                        <a
                          href={child.link}
                          className={styles.subrow}
                          onClick={() => onCategoryClick(child)}
                        >
                          {child.name}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
})

export default Flyout
